import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Home } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const copy = {
  fr: {
    heading: "Merci du fond du cœur !",
    subheading: "Votre don a été reçu avec succès.",
    message: "Grâce à votre générosité, nous pouvons continuer à soutenir les jeunes filles et les survivantes de violence, d'abus et de traumatisme partout dans le monde.",
    receipt: "Un reçu de votre don vous sera envoyé par courriel dans les prochaines minutes.",
    backHome: "Retour à l'accueil",
    aboutUs: "En savoir plus sur nous",
  },
  en: {
    heading: "Thank you from the bottom of our hearts!",
    subheading: "Your donation has been successfully received.",
    message: "Thanks to your generosity, we can continue to support young girls and survivors of violence, abuse, and trauma around the world.",
    receipt: "A receipt for your donation will be emailed to you within the next few minutes.",
    backHome: "Back to Home",
    aboutUs: "Learn more about us",
  },
};

export default function DonationConfirmation() {
  const { language } = useLanguage();
  const t = copy[language] || copy.fr;

  // If Stripe redirected here with ?session_id=, confirm the donation server-side
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      let attempts = 0;
      while (attempts < 5 && !cancelled) {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/donate/stripe/status/${encodeURIComponent(sessionId)}`);
          if (res.data?.payment_status === 'paid') return;
        } catch (e) { /* silent */ }
        await new Promise((r) => setTimeout(r, 2000));
        attempts += 1;
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      data-testid="donation-confirmation-page"
      className="min-h-screen bg-gradient-to-br from-[#FDF2F8] via-white to-[#FFF6FA] flex items-center justify-center px-4 py-12"
    >
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-[#CD0000]/10 p-8 md:p-10 text-center">
        <div className="w-20 h-20 bg-[#CD0000]/10 rounded-full flex items-center justify-center mx-auto mb-6 ring-8 ring-[#CD0000]/5 animate-heartbeat">
          <Heart className="w-11 h-11 text-[#CD0000]" fill="#CD0000" />
        </div>

        <h1 className="text-3xl md:text-4xl font-extrabold text-[#1D1D1D] mb-3 leading-tight">
          {t.heading}
        </h1>
        <p className="text-lg text-[#CD0000] font-semibold mb-6">{t.subheading}</p>

        <p className="text-gray-700 leading-relaxed mb-5">{t.message}</p>

        <div className="bg-[#FDF2F8] border border-[#CD0000]/15 rounded-xl p-4 text-sm text-gray-700 mb-8">
          {t.receipt}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            data-testid="donation-confirmation-home-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#CD0000] hover:bg-[#A00000] text-white font-bold py-3 px-6 rounded-xl transition-colors"
          >
            <Home className="w-4 h-4" />
            {t.backHome}
          </Link>
          <Link
            to="/events"
            data-testid="donation-confirmation-events-btn"
            className="inline-flex items-center justify-center gap-2 bg-white text-[#1D1D1D] border border-gray-200 hover:border-[#CD0000]/50 font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            {t.aboutUs}
          </Link>
        </div>
      </div>
    </div>
  );
}
